import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, ClipboardList, OctagonAlert, Play } from 'lucide-react';
import {
  ASSIGNMENT_PROBLEM_KINDS,
  MY_WORK_FILTERS,
  type AssignmentProblemKind,
  type MyWorkFilter,
  type MyWorkItem,
} from '@tailoring-crm/shared';
import { EmptyState } from '../components/feedback/empty-state.js';
import { LoadError } from '../components/feedback/load-error.js';
import { PageHeader } from '../components/layout/page-header.js';
import { CardThumbnail } from '../components/kanban/card-thumbnail.js';
import { SketchPicture } from '../components/measurements/sketch-picture.js';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  ListSkeleton,
  useToast,
  cn,
} from '@tailoring-crm/ui';
import {
  useCompleteJob,
  useFlagProblem,
  useMyWork,
  useStartJob,
} from '../lib/assignments/use-assignments.js';
import { formatDate, formatMinutes } from '../lib/format.js';

/**
 * The karigar's screen — a different product from the owner's, on a cheap Android phone.
 *
 * Everything about the layout follows from where it is used: standing at a table, one hand free,
 * in a workshop. So it is a single vertical list with no horizontal scrolling, the measurements
 * are open on the card rather than behind a tap (they are the thing being read, constantly), and
 * the two actions that matter — start and done — are full-width buttons at the bottom of each
 * job where a thumb reaches.
 *
 * There is no money on this page, and there is no way to get to any: the data it renders is
 * served by an endpoint whose payload has none. See `my-work.service.ts` on the API side.
 *
 * The third button is the one that makes the feature work at all. A karigar who cannot proceed
 * has, until now, put the garment down and said nothing; "Report a problem" is the thing that
 * turns three silent days into an owner seeing a red banner on the board.
 */

const FILTER_ICONS = { open: ClipboardList, today: ClipboardList, done: CheckCircle2 } as const;

export function MyWorkPage(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();

  const [filter, setFilter] = useState<MyWorkFilter>('open');
  const [flagging, setFlagging] = useState<MyWorkItem | null>(null);
  const [problemKind, setProblemKind] = useState<AssignmentProblemKind>('wrong_measurement');
  const [note, setNote] = useState('');

  const work = useMyWork(filter);
  const start = useStartJob();
  const complete = useCompleteJob();
  const flag = useFlagProblem();

  const report = (error: unknown): void => {
    toast({
      title: error instanceof Error ? error.message : t('error.unexpected'),
      variant: 'destructive',
    });
  };

  const onStart = async (job: MyWorkItem): Promise<void> => {
    try {
      await start.mutateAsync(job.assignmentId);
      toast({ title: t('myWork.started', { garment: job.garmentTypeLabel }), variant: 'success' });
    } catch (error) {
      report(error);
    }
  };

  const onComplete = async (job: MyWorkItem): Promise<void> => {
    try {
      const result = await complete.mutateAsync(job.assignmentId);
      toast({
        title: t('myWork.completed', { garment: job.garmentTypeLabel }),
        ...(result.stageAdvanced
          ? { description: t('myWork.movedOn', { stage: t(`stage.${result.stage}`) }) }
          : {}),
        variant: 'success',
      });
    } catch (error) {
      report(error);
    }
  };

  const onFlag = async (): Promise<void> => {
    if (!flagging) return;
    try {
      await flag.mutateAsync({
        assignmentId: flagging.assignmentId,
        input: { kind: problemKind, ...(note.trim() ? { note: note.trim() } : {}) },
      });
      toast({ title: t('myWork.problemRaised'), variant: 'success' });
    } catch (error) {
      report(error);
    } finally {
      setFlagging(null);
      setNote('');
    }
  };

  const counts = work.data?.counts;
  const jobs = work.data?.items ?? [];
  const busy = start.isPending || complete.isPending;

  return (
    <>
      <PageHeader title={t('myWork.title')} description={t('myWork.description')} />

      <div className="flex flex-col gap-4">
        {/* Three tabs, thumb-sized, no horizontal scroll — there are only ever three. */}
        <div role="tablist" aria-label={t('myWork.title')} className="grid grid-cols-3 gap-2">
          {MY_WORK_FILTERS.map((one) => {
            const active = filter === one;
            const Icon = FILTER_ICONS[one];
            const count =
              one === 'open'
                ? counts?.open
                : one === 'today'
                  ? (counts?.today ?? 0) + (counts?.overdue ?? 0)
                  : counts?.completedToday;
            return (
              <button
                key={one}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setFilter(one)}
                className={cn(
                  'flex min-h-11 items-center justify-center gap-1.5 rounded-lg border px-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  active
                    ? 'border-transparent bg-primary text-primary-foreground'
                    : 'border-border hover:bg-accent',
                )}
              >
                <Icon aria-hidden="true" className="size-4 shrink-0" />
                {t(`myWork.filter.${one}`)}
                {count !== undefined && count > 0 ? (
                  <span
                    className={cn(
                      'rounded-full px-1.5 text-xs tabular-nums',
                      active ? 'bg-primary-foreground/20' : 'bg-muted',
                    )}
                  >
                    {count}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>

        {counts && counts.overdue > 0 ? (
          <p role="status" className="text-sm">
            <Badge variant="destructive">
              {t('myWork.overdueCount', { count: counts.overdue })}
            </Badge>
          </p>
        ) : null}

        {work.isPending ? (
          <ListSkeleton rows={3} />
        ) : work.isError ? (
          <LoadError onRetry={() => void work.refetch()} />
        ) : jobs.length === 0 ? (
          <EmptyState
            icon={ClipboardList}
            title={t(`myWork.empty.${filter}Title`)}
            description={t('myWork.emptyDescription')}
          />
        ) : (
          <ul className="flex flex-col gap-3">
            {jobs.map((job) => (
              <li
                key={job.assignmentId}
                className={cn(
                  'flex flex-col gap-3 rounded-lg border p-3',
                  job.dueState === 'overdue'
                    ? 'border-destructive/60 bg-destructive/5'
                    : job.dueState === 'today'
                      ? 'border-warning/60 bg-warning/5'
                      : 'border-border',
                )}
              >
                <div className="flex items-start gap-3">
                  <CardThumbnail mediaId={job.mediaIds[0] ?? null} alt={job.garmentTypeLabel} />
                  <div className="min-w-0 flex-1">
                    <p className="text-base font-semibold">
                      {job.quantity} × {job.garmentTypeLabel}
                    </p>
                    {/* First name only. A karigar needs to tell two lehengas apart, not to be
                        handed the shop's customer book. */}
                    <p className="text-sm text-muted-foreground">
                      {job.customerFirstName} · {job.orderNumber}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-1.5 text-xs">
                  <Badge variant="outline">{t(`stage.${job.stage}`)}</Badge>
                  {job.priority ? <Badge variant="destructive">{t('orders.urgent')}</Badge> : null}
                  {job.dueState === 'overdue' ? (
                    <Badge variant="destructive">
                      {t('orders.overdueBy', { date: formatDate(job.promisedDate ?? '') })}
                    </Badge>
                  ) : job.dueState === 'today' ? (
                    <Badge variant="warning">{t('orders.dueToday')}</Badge>
                  ) : job.promisedDate ? (
                    <span className="text-muted-foreground">{formatDate(job.promisedDate)}</span>
                  ) : null}
                  {job.status === 'in_progress' ? (
                    <Badge variant="secondary">{t('assignments.status.in_progress')}</Badge>
                  ) : null}
                  {/* How long this is meant to take, hh:mm. A job nobody estimated says so —
                      showing 00:00 would read as "this is nothing", which is a different claim. */}
                  <span className="text-muted-foreground tabular-nums">
                    {job.estimatedMinutes === null
                      ? t('myWork.estimateNone')
                      : t('myWork.estimate', { duration: formatMinutes(job.estimatedMinutes) })}
                  </span>
                </div>

                {job.notes ? (
                  <p className="rounded-md bg-muted/60 p-2 text-sm">{job.notes}</p>
                ) : null}

                {/* Open, not behind a tap: this is what is being read while stitching. */}
                {job.measurements ? (
                  <>
                    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-md bg-muted/50 p-3 text-sm sm:grid-cols-3">
                      {job.measurements.values.map((value) => (
                        <div key={value.fieldKey} className="flex justify-between gap-2">
                          <dt className="text-muted-foreground">{value.label}</dt>
                          <dd className="font-medium tabular-nums">{value.display}</dd>
                        </div>
                      ))}
                    </dl>
                    {/* The drawing frozen with them — the shape the numbers are of. A picture,
                        tapped to see it full size; a karigar reads it, they do not redraw it. */}
                    {job.measurements.sketch ? (
                      <SketchPicture
                        previewMediaId={job.measurements.sketch.previewMediaId}
                        name={job.measurements.profileName}
                      />
                    ) : null}
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('orders.noMeasurements')}</p>
                )}

                {job.mediaIds.length > 1 ? (
                  <ul className="-mx-1 flex gap-2 overflow-x-auto px-1">
                    {job.mediaIds.slice(1).map((mediaId) => (
                      <li key={mediaId} className="shrink-0">
                        <CardThumbnail mediaId={mediaId} alt={job.garmentTypeLabel} />
                      </li>
                    ))}
                  </ul>
                ) : null}

                {job.problem && job.problem.resolvedAt === null ? (
                  <p className="flex items-start gap-1.5 rounded-md border border-destructive/60 p-2 text-sm">
                    <OctagonAlert
                      aria-hidden="true"
                      className="mt-0.5 size-4 shrink-0 text-destructive"
                    />
                    <span>
                      {t(`assignments.problemKind.${job.problem.kind}`)}
                      {job.problem.note ? ` — ${job.problem.note}` : ''}
                      <span className="block text-muted-foreground">
                        {t('myWork.problemWaiting')}
                      </span>
                    </span>
                  </p>
                ) : null}

                {/* Full-width buttons at the bottom: one hand, one thumb, a cheap screen. */}
                {job.status === 'completed' ? (
                  <p className="text-sm text-muted-foreground">
                    {t('assignments.completedOn', { date: formatDate(job.completedAt ?? '') })}
                  </p>
                ) : (
                  <div className="flex flex-col gap-2">
                    {job.status === 'assigned' ? (
                      <Button block disabled={busy} onClick={() => void onStart(job)}>
                        <Play aria-hidden="true" className="mr-1.5 size-4" />
                        {t('myWork.start')}
                      </Button>
                    ) : (
                      <Button block disabled={busy} onClick={() => void onComplete(job)}>
                        <CheckCircle2 aria-hidden="true" className="mr-1.5 size-4" />
                        {t('myWork.complete')}
                      </Button>
                    )}
                    {!job.problem || job.problem.resolvedAt !== null ? (
                      <Button
                        block
                        variant="outline"
                        onClick={() => {
                          setFlagging(job);
                          setProblemKind('wrong_measurement');
                        }}
                      >
                        <OctagonAlert aria-hidden="true" className="mr-1.5 size-4" />
                        {t('myWork.reportProblem')}
                      </Button>
                    ) : null}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {flagging ? (
        <Dialog open onOpenChange={(open) => !open && setFlagging(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {t('myWork.problemTitle', { garment: flagging.garmentTypeLabel })}
              </DialogTitle>
              <DialogDescription>{t('myWork.problemHint')}</DialogDescription>
            </DialogHeader>

            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 text-sm font-medium">{t('myWork.problemKindLabel')}</legend>
              {ASSIGNMENT_PROBLEM_KINDS.map((kind) => (
                <label
                  key={kind}
                  className={cn(
                    'flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border p-3 text-sm transition-colors',
                    problemKind === kind
                      ? 'border-primary bg-accent'
                      : 'border-border hover:bg-accent',
                  )}
                >
                  <input
                    type="radio"
                    name="problem-kind"
                    value={kind}
                    checked={problemKind === kind}
                    onChange={() => setProblemKind(kind)}
                    className="size-4 shrink-0 accent-primary"
                  />
                  {t(`assignments.problemKind.${kind}`)}
                </label>
              ))}
            </fieldset>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="problem-note">{t('myWork.problemNoteLabel')}</Label>
              <Input
                id="problem-note"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                placeholder={t('myWork.problemNotePlaceholder')}
              />
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setFlagging(null)}>
                {t('common.cancel')}
              </Button>
              <Button disabled={flag.isPending} onClick={() => void onFlag()}>
                {t('myWork.problemConfirm')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}

export default MyWorkPage;
